import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { KeyRound, Save, Globe } from "lucide-react";

export default function SuperAdminSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/superadmin/settings");
      setForm(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load settings");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/superadmin/settings", form);
      setForm(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!form) {
    return <div className="space-y-4" data-testid="super-settings-loading">
      <div className="h-8 w-48 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
      <div className="h-56 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
    </div>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="super-settings-page">
      <div>
        <h1 className="font-display text-3xl">Platform Settings</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Billing-wide configuration: payment gateway keys, downgrade policy, default plan.</p>
      </div>

      <section className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-4" data-testid="platform-domain-section">
        <h2 className="font-display text-xl flex items-center gap-2"><Globe size={18} /> Platform base domain</h2>
        <p className="text-xs text-[hsl(var(--kb-muted))]">
          The apex domain every tenant&apos;s subdomain points to. Example: setting <code>kalkibazaar.com</code> turns every
          tenant&apos;s storefront into <code>{"{slug}"}.kalkibazaar.com</code> (assuming wildcard DNS + SSL are in place).
          Leave empty to keep using the current request host.
        </p>
        <div>
          <label className="text-sm font-medium">Platform base domain</label>
          <input
            className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            placeholder="e.g. kalkibazaar.com"
            value={form.platform_base_domain || ""}
            onChange={(e) => setForm({ ...form, platform_base_domain: e.target.value.trim().toLowerCase() })}
            data-testid="platform-base-domain"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Public CNAME target (for custom-domain tenants)</label>
          <input
            className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            placeholder="e.g. ingress.kalkibazaar.com"
            value={form.public_cname_target || ""}
            onChange={(e) => setForm({ ...form, public_cname_target: e.target.value.trim().toLowerCase() })}
            data-testid="public-cname-target"
          />
          <p className="text-[10px] text-[hsl(var(--kb-muted))] mt-1">
            Tenants who connect their own domain will be asked to point a CNAME here. Used for DNS verification on /admin/domain.
          </p>
        </div>
      </section>

      <section className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-4">
        <h2 className="font-display text-xl flex items-center gap-2"><KeyRound size={18} /> Razorpay (mocked for now)</h2>
        <p className="text-xs text-[hsl(var(--kb-muted))]">These keys are stored but the billing system is currently mocked. Once real charges go live, these will be used as the Razorpay API credentials.</p>
        <div>
          <label className="text-sm font-medium">Key ID</label>
          <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            placeholder="rzp_live_..."
            value={form.razorpay_key_id || ""}
            onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value })}
            data-testid="razorpay-key-id" />
        </div>
        <div>
          <label className="text-sm font-medium">Key Secret</label>
          <input type="password" className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            placeholder="••••••••"
            value={form.razorpay_key_secret || ""}
            onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value })}
            data-testid="razorpay-key-secret" />
        </div>
      </section>

      <section className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-4">
        <h2 className="font-display text-xl">Billing policy</h2>
        <div>
          <label className="text-sm font-medium">Downgrade policy</label>
          <select className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
            value={form.downgrade_policy}
            onChange={(e) => setForm({ ...form, downgrade_policy: e.target.value })}
            data-testid="downgrade-policy">
            <option value="end_of_cycle">Keep active until current cycle ends (no refund)</option>
            <option value="immediate">Cancel immediately</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Default plan slug for new tenants</label>
          <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
            value={form.default_plan_slug || ""}
            onChange={(e) => setForm({ ...form, default_plan_slug: e.target.value })}
            data-testid="default-plan-slug" />
        </div>
        <div>
          <label className="text-sm font-medium">Billing currency</label>
          <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
            value={form.billing_currency || "INR"}
            onChange={(e) => setForm({ ...form, billing_currency: e.target.value.toUpperCase() })} />
        </div>
      </section>

      <button onClick={save} disabled={saving} className="kb-btn-primary inline-flex items-center gap-2" data-testid="settings-save-btn">
        <Save size={14} /> {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}
